import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/adminApi';
// Database service import removed - database functionality disabled

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const seedInbox = [
  {
    id: 'seed-1',
    accountId: '',
    from: 'ops@shipperdemo.com',
    to: 'dispatch@yourcompany.com',
    subject: 'Rate request for MC123456',
    body: 'Hi team, looking for capacity on ATL > DFW 2x weekly. Can you assist?',
    status: 'unread',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    tags: ['carrier', 'lane'],
  },
  {
    id: 'seed-2',
    accountId: '',
    from: 'owner@carrierpro.net',
    to: 'support@yourcompany.com',
    subject: 'Paperwork + COI request',
    body: 'Please share COI and W9. We are MC 789123 and ready for onboarding.',
    status: 'read',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 20).toISOString(),
    tags: ['documents'],
  },
];

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const normalizeEmails = (input) => {
  const pool = Array.isArray(input)
    ? input.flatMap((item) => String(item || '').split(/[\n,;]+/))
    : String(input || '')
        .split(/[\n,;]+/)
        .map((item) => item.trim());
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const cleaned = pool
    .map((v) => {
      const m = v.match(/<([^>]+)>/);
      return m ? m[1].trim() : v.trim();
    })
    .map((v) => v.toLowerCase())
    .filter((v) => regex.test(v));
  return Array.from(new Set(cleaned));
};

const applyMergeTokens = (text, row) => {
  if (!text || !row) return text || '';
  let result = String(text);
  Object.entries(row).forEach(([key, value]) => {
    const token = new RegExp(`{{\\s*${String(key).replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\s*}}`, 'gi');
    result = result.replace(token, String(value ?? ''));
  });
  if (row.email) {
    result = result.replace(/{{\s*email\s*}}/gi, String(row.email));
  }
  return result;
};

export const useEmailManager = () => {
  const { isAuthenticated, token } = useAuth();
  const envApiBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || '';
  const apiBase = useMemo(() => {
    if (envApiBase) return envApiBase.replace(/\/$/, '');
    if (typeof window === 'undefined') return '';
    return window.location.origin || '';
  }, [envApiBase]);
  const apiUrl = useCallback((path) => (apiBase ? `${apiBase}${path}` : path), [apiBase]);
  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}`, 'X-Auth-Token': token } : {}),
    [token]
  );
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [inbox, setInbox] = useState(seedInbox);
  const [sent, setSent] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [replyDrafts, setReplyDrafts] = useState({});
  const [filterUnread, setFilterUnread] = useState(false);
  const [fetchingInbox, setFetchingInbox] = useState(false);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [prefsError, setPrefsError] = useState('');
  const accountsRef = useRef([]);

  useEffect(() => {
    accountsRef.current = accounts;
  }, [accounts]);

  useEffect(() => {
    if (!selectedAccountId && accounts[0]) {
      setSelectedAccountId(accounts[0].id);
    }
  }, [accounts, selectedAccountId]);

  const addAccount = useCallback((payload) => {
    const record = {
      id: payload.id || uid(), // Use existing id if provided, otherwise generate new one
      createdAt: payload.createdAt || new Date().toISOString(),
      status: payload.status || 'connected',
      ...payload,
    };
    setAccounts((prev) => {
      const next = [record, ...prev];
      accountsRef.current = next;
      if (isAuthenticated) {
        adminApi.saveAccounts(next, token).catch((e) => {
          const msg = e?.message || 'Failed to save accounts';
          setPrefsError(msg);
        });
      }
      return next;
    });
    setSelectedAccountId(record.id);
    return record;
  }, [isAuthenticated, token]);

  const updateAccount = useCallback((id, updates) => {
    setAccounts((prev) => {
      const next = prev.map((acc) =>
        acc.id === id ? { ...acc, ...updates, updatedAt: new Date().toISOString() } : acc
      );
      accountsRef.current = next;
      if (isAuthenticated) {
        adminApi.saveAccounts(next, token).catch((e) => {
          const msg = e?.message || 'Failed to save accounts';
          setPrefsError(msg);
        });
      }
      return next;
    });
  }, [isAuthenticated, token]);

  const deleteAccount = useCallback(
    (id) => {
      setAccounts((prev) => {
        const next = prev.filter((acc) => acc.id !== id);
        accountsRef.current = next;
        if (selectedAccountId === id) {
          setSelectedAccountId(next[0]?.id || '');
        }
        if (isAuthenticated) {
          adminApi.saveAccounts(next, token).catch((e) => {
            const msg = e?.message || 'Failed to save accounts';
            setPrefsError(msg);
          });
        }
        return next;
      });
    },
    [selectedAccountId, isAuthenticated, token]
  );

  // Server persistence of accounts/messages when authenticated
  const loadPrefsFromServer = useCallback(async () => {
    if (!isAuthenticated) {
      setPrefsLoaded(true);
      return;
    }
    try {
      const [prefsRes, accountsRes] = await Promise.all([
        adminApi.loadPrefs(token).catch(() => ({})),
        adminApi.loadAccounts(token).catch(() => ({})),
        // Database message loading removed - database functionality disabled
      ]);
      setPrefsError('');
      if (prefsRes?.prefs) {
        if (prefsRes.prefs.selectedAccountId) setSelectedAccountId(prefsRes.prefs.selectedAccountId);
      }
      if (
        Array.isArray(accountsRes?.accounts) &&
        (accountsRes.accounts.length > 0 || accountsRef.current.length === 0)
      ) {
        setAccounts(accountsRes.accounts);
      }
    } catch (e) {
      const msg = e?.message || 'Failed to load preferences';
      setPrefsError(msg);
      console.warn('Prefs load failed', e);
    } finally {
      setPrefsLoaded(true);
    }
  }, [isAuthenticated, token]);

  const syncPrefsToServer = useCallback(async () => {
    if (!isAuthenticated || !prefsLoaded) return;
    try {
      await adminApi.savePrefs({ selectedAccountId }, token);
      setPrefsError('');
    } catch (e) {
      const msg = e?.message || 'Failed to save preferences';
      setPrefsError(msg);
      console.warn('Prefs save failed', e);
    }
  }, [isAuthenticated, token, prefsLoaded, accounts, inbox, sent, selectedAccountId]);

  useEffect(() => {
    if (isAuthenticated) {
      setPrefsLoaded(false);
      loadPrefsFromServer();
    } else {
      setPrefsLoaded(true);
    }
  }, [isAuthenticated, loadPrefsFromServer]);

  useEffect(() => {
    syncPrefsToServer();
  }, [accounts, inbox, sent, selectedAccountId, syncPrefsToServer]);

  const resolveEmailEndpoint = () => {
    if (apiBase) {
      return `${apiBase.replace(/\/$/, '')}/api/email_send.php`;
    }
    if (typeof window === 'undefined') return '/api/email_send.php';
    const { origin, port } = window.location;
    const isLocal = /https?:\/\/localhost(?::\d+)?/i.test(origin);
    if (isLocal && port === '3000') {
      return 'http://localhost:8080/api/email_send.php';
    }
    return '/api/email_send.php';
  };

  const testAccount = useCallback(
    async (accountLike) => {
      const acc =
        typeof accountLike === 'string'
          ? accounts.find((a) => a.id === accountLike)
          : accountLike;
      if (!acc) {
        throw new Error('Select or save an account first');
      }
      const endpoint = resolveEmailEndpoint();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'ping',
          account: {
            host: acc.host,
            port: acc.port,
            secure: acc.secure,
            username: acc.username || acc.fromEmail,
            password: acc.password,
            fromEmail: acc.fromEmail || acc.username,
            fromName: acc.fromName || acc.label,
          },
        }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.ok) {
        const msg = payload?.error || `SMTP test failed (${res.status})`;
        throw new Error(msg);
      }
      return {
        ok: true,
        message: payload?.message || `Connected to ${acc.provider || 'SMTP'} as ${acc.fromEmail || acc.username}`,
      };
    },
    [accounts]
  );

  const sendEmail = useCallback(
    async ({
      subject,
      body,
      bodyText,
      bodyHtml,
      recipients,
      accountId,
      replyToId,
      attachments = [],
      mergeData = null,
      campaignId = '',
      trackOpens = true,
    }) => {
      const normalizedRecipients = normalizeEmails(recipients || []);
      if (!normalizedRecipients.length) {
        throw new Error('Add at least one recipient');
      }
      const account =
        accounts.find((acc) => acc.id === (accountId || selectedAccountId)) ||
        accounts[0];
      if (!account) {
        throw new Error('Pick an email account first');
      }

      const endpoint = resolveEmailEndpoint();
      const sendSingle = async (recipient) => {
        const row = mergeData && mergeData[recipient] ? mergeData[recipient] : null;
        const subj = row ? applyMergeTokens(subject || '', row) : (subject || '');
        const html = row ? applyMergeTokens(bodyHtml || '', row) : (bodyHtml || '');
        const text = row
          ? applyMergeTokens(bodyText || body || '', row)
          : (bodyText || body || '');
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          credentials: 'include',
          body: JSON.stringify({
            account: {
              host: account.host,
              port: account.port,
              secure: account.secure,
              username: account.username || account.fromEmail,
              password: account.password,
              fromEmail: account.fromEmail || account.username,
              fromName: account.fromName || account.label || account.fromEmail || account.username,
            },
            recipients: [recipient],
            subject: subj,
            body: text,
            bodyText: text,
            bodyHtml: html,
            attachments,
            replyToId,
            campaignId,
            trackOpens,
          }),
        });
        const raw = await res.text();
        let payload = null;
        if (raw) {
          try {
            payload = JSON.parse(raw);
          } catch (err) {
            payload = null;
          }
        }
        if (!payload && res.ok) {
          payload = { ok: true };
        }
        if (!res.ok || !payload?.ok) {
          const msg = payload?.error || raw || `SMTP send failed (${res.status})`;
          throw new Error(msg);
        }
        const mid = payload?.messageId || uid();
        const record = {
          id: mid,
          messageId: mid,
          trackingId: payload?.trackingId || payload?.messageId || mid,
          trackingError: payload?.trackingError || '',
          accountId: account.id,
          subject: subj,
          body: html || text,
          recipients: [recipient],
          status: 'sent',
          parentId: replyToId || null,
          sentAt: new Date().toISOString(),
        };
        setSent((prev) => [record, ...prev].slice(0, 200));
        return record;
      };

      setSending(true);
      setStatusText('');
      const showProgress = normalizedRecipients.length > 1;
      if (showProgress) {
        setSendProgress({
          total: normalizedRecipients.length,
          sent: 0,
          failed: 0,
          current: 0,
        });
      } else {
        setSendProgress(null);
      }

      let sentCount = 0;
      let failedCount = 0;
      const failedRecipients = [];
      try {
        const records = [];
        for (let i = 0; i < normalizedRecipients.length; i += 1) {
          const recipient = normalizedRecipients[i];
          try {
            const record = await sendSingle(recipient);
            records.push(record);
            sentCount += 1;
          } catch (err) {
            failedCount += 1;
            failedRecipients.push(recipient);
          }
          if (showProgress) {
            setSendProgress((prev) =>
              prev
                ? {
                    ...prev,
                    sent: sentCount,
                    failed: failedCount,
                    current: i + 1,
                  }
                : prev
            );
          }
        }

        setStatusText(
          failedCount
            ? `Sent ${sentCount}, failed ${failedCount} (${failedRecipients.slice(0, 3).join(', ')}${failedRecipients.length > 3 ? '…' : ''}).`
            : `Sent to ${sentCount} recipients.`
        );

        if (sentCount === 0) {
          throw new Error('Failed to send any emails.');
        }

        return records.length === 1 ? records[0] : { ok: true, sentCount, failedCount };
      } finally {
        setSending(false);
      }
    },
    [accounts, selectedAccountId, authHeaders]
  );

  const fetchInbox = useCallback(
    async (accountIdOverride) => {
      const account =
        accounts.find((acc) => acc.id === (accountIdOverride || selectedAccountId)) ||
        accounts[0];
      if (!account) throw new Error('Pick an email account first');
      if (!account.imapHost && !account.popHost) throw new Error('IMAP/POP host not set for this account');
      setFetchingInbox(true);
      try {
        const endpoint = resolveEmailEndpoint().replace('email_send.php', 'email_fetch.php');
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            account: {
              imapHost: account.imapHost,
              imapPort: account.imapPort || 993,
              imapSecure: account.imapSecure !== false,
              popHost: account.popHost,
              popPort: account.popPort || 995,
              popSecure: account.popSecure !== false,
              username: account.username || account.fromEmail,
              password: account.password,
              mailbox: account.mailbox || 'INBOX',
            },
          }),
        });
        const raw = await res.text();
        let payload = null;
        try {
          payload = raw ? JSON.parse(raw) : null;
        } catch (err) {
          throw new Error('Invalid response from inbox server.');
        }
        if (!res.ok || !payload?.ok) {
          throw new Error(payload?.error || `IMAP fetch failed (${res.status})`);
        }
        const msgs = Array.isArray(payload.messages) ? payload.messages : [];
        setInbox((prev) => {
          const existingIds = new Set((prev || []).map((m) => m.id));
          const merged = [...prev];
          msgs.forEach((m) => {
            if (!existingIds.has(m.id)) merged.unshift({ ...m, status: 'unread' });
          });
          return merged;
        });
        return msgs;
      } finally {
        setFetchingInbox(false);
      }
    },
    [accounts, selectedAccountId]
  );

  const appendInbox = useCallback((message) => {
    setInbox((prev) => [
      {
        id: uid(),
        timestamp: new Date().toISOString(),
        status: 'unread',
        ...message,
      },
      ...prev,
    ]);
  }, []);

  const fetchTrackingStats = useCallback(async (messageId) => {
    if (!messageId) throw new Error('messageId required');
    const endpoint = resolveEmailEndpoint().replace('email_send.php', 'track_stats.php');
    const url = `${endpoint}?id=${encodeURIComponent(messageId)}`;
    const res = await fetch(url, { headers: authHeaders });
    const payload = await res.json();
    if (!res.ok || !payload?.ok) {
      throw new Error(payload?.error || `Tracking fetch failed (${res.status})`);
    }
    return payload;
  }, [authHeaders]);

  const fetchTrackingTotals = useCallback(async () => {
    const endpoint = resolveEmailEndpoint().replace('email_send.php', 'track_stats.php');
    const res = await fetch(endpoint, { headers: authHeaders });
    const payload = await res.json();
    if (!res.ok || !payload?.ok) {
      throw new Error(payload?.error || `Tracking fetch failed (${res.status})`);
    }
    return payload;
  }, [authHeaders]);

  const setMessageStatus = useCallback((id, status) => {
    setInbox((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, status } : msg))
    );
  }, []);

  const replyToMessage = useCallback(
    async (messageId, replyBody, accountId) => {
      const message = inbox.find((m) => m.id === messageId);
      if (!message) throw new Error('Message not found');
      return sendEmail({
        subject: `Re: ${message.subject}`,
        body: replyBody,
        recipients: [message.from],
        accountId: accountId || message.accountId || selectedAccountId,
      });
    },
    [inbox, sendEmail, selectedAccountId]
  );

  const deleteMessage = useCallback((id) => {
    setInbox((prev) => prev.filter((m) => m.id !== id));
    // Also drop any sent replies tied to this message so the conversation disappears locally
    setSent((prev) => prev.filter((s) => s.parentId !== id && s.id !== id));
  }, []);

  const stats = useMemo(() => {
    const unread = inbox.filter((m) => m.status === 'unread').length;
    return { unread, totalInbox: inbox.length, totalSent: sent.length };
  }, [inbox, sent]);

  return {
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    inbox,
    sent,
    sending,
    statusText,
    addAccount,
    updateAccount,
    deleteAccount,
    testAccount,
    sendEmail,
    fetchInbox,
    appendInbox,
    fetchTrackingStats,
    fetchTrackingTotals,
    setMessageStatus,
    replyToMessage,
    deleteMessage,
    stats,
    prefsError,
    filterUnread,
    setFilterUnread,
    fetchingInbox,
    replyDrafts,
    setReplyDrafts,
    sendProgress,
  };
};
